'use client'

/**
 * What the admin sign-in page draws, from the API's "which methods" answer (branded sign-in, 3 Nov 2026; contract
 * docs/superpowers/specs/2026-11-03-branded-sign-in-design.md sections 4.4 and 4.5, screens A1 to A8).
 *
 * HumanFirewall is a security-awareness platform; a customer company chooses how its admins sign in. The web never
 * decides what is allowed: the API answers, and every refusal is the server's. This file turns the answer into the
 * pieces the page draws (the form tabs, the buttons that leave for a provider, what is down) and reads it:
 *   - by the address the page is served on, before anything is drawn, so a company address never flashes the methods
 *     its company switched off (A3 to A5, A7);
 *   - by the typed work e-mail's DOMAIN on dash (A1, A2). The API answers the same bytes for every address of one
 *     domain, so this cannot be used to find out who has an account.
 * An answer that does not come (an older API, the network) is today's page: the server still enforces every switch.
 */

import { useEffect, useState } from 'react'
import {
  fetchSignInOptions,
  fetchSignInOptionsByEmail,
  type SignInOptions,
} from '@/lib/sign-in/options-api'
import {
  HF_APP_METHOD_KEYS,
  ORG_METHOD_PROVIDER,
  inSignInPageOrder,
  isOrgMethod,
  type HfAppMethodKey,
  type OrgMethodKey,
  type SignInAudienceKey,
  type SignInMethodKey,
} from '@/lib/sign-in/methods'

/** A method that leaves the page for a provider and comes back through a one-time code. */
export type RedirectMethodKey = OrgMethodKey | HfAppMethodKey

/** The form tabs, in today's order. */
export type FormTab = 'password' | 'otp' | 'passkey'

export interface RedirectButton {
  method: RedirectMethodKey
  provider: 'microsoft' | 'google' | 'saml' | 'oidc'
  /** The company's own name for it ("CSB Microsoft"), or null for HumanFirewall's shared app (worded by the page). */
  label: string | null
  /**
   * In a row beside the form the provider's name alone is clear ("Microsoft" on CSB's page is CSB's app, A3), unless a
   * HumanFirewall app of the same provider is offered too; then the company's label tells them apart.
   */
  shortLabel: string | null
}

export interface UnavailableApp {
  method: OrgMethodKey
  provider: 'microsoft' | 'google' | 'saml' | 'oidc'
  label: string | null
}

export interface SignInView {
  company: { name: string; logoUrl: string | null }
  hostKind: SignInOptions['hostKind']
  /** The form tabs to draw. A passkey is a tab only beside a password or an e-mail code (A3); never on an own domain. */
  tabs: FormTab[]
  /** Passkey offered but not as a tab: a small link under a provider button (A4), or a button of its own (A7). */
  passkeyAlone: 'link' | 'button' | null
  redirects: RedirectButton[]
  unavailable: UnavailableApp[]
  emergency: boolean
}

const PROVIDER_OF_HF: Readonly<Record<HfAppMethodKey, 'microsoft' | 'google'>> = {
  hf_microsoft: 'microsoft',
  hf_google: 'google',
}

function isHfApp(key: string): key is HfAppMethodKey {
  return (HF_APP_METHOD_KEYS as readonly string[]).includes(key)
}

/** The answer as a company's page, or null for today's page (scope 'platform', or nothing usable). */
export function signInViewOf(options: SignInOptions | null): SignInView | null {
  if (!options || options.scope !== 'company' || !options.company) return null
  const offered = new Set<SignInMethodKey>(options.offered)
  const ownDomain = options.hostKind === 'customer_cname'
  const passkey = offered.has('passkey') && !ownDomain
  const labelOf = (method: OrgMethodKey): string | null =>
    options.connections.find((c) => c.method === method)?.label?.trim() || null

  // An app that is down (A7) is said to be down, never drawn as a button that fails. The company's own apps come
  // before HumanFirewall's shared ones (SIGN_IN_PAGE_ORDER, the rule C1's preview draws too; evidence run X4a).
  const down = new Set<string>(options.unavailable)
  const redirectKeys = inSignInPageOrder(
    options.offered.filter((k): k is RedirectMethodKey => (isOrgMethod(k) || isHfApp(k)) && !down.has(k)),
  )
  const hfProviders = new Set(redirectKeys.filter(isHfApp).map((k) => PROVIDER_OF_HF[k]))
  const redirects: RedirectButton[] = redirectKeys.map((method) => {
    if (isHfApp(method)) return { method, provider: PROVIDER_OF_HF[method], label: null, shortLabel: null }
    const provider = ORG_METHOD_PROVIDER[method]
    const label = labelOf(method)
    const providerAlone = (provider === 'microsoft' || provider === 'google') && !hfProviders.has(provider)
    return { method, provider, label, shortLabel: providerAlone ? null : label }
  })

  const tabs: FormTab[] = []
  if (offered.has('password')) tabs.push('password')
  if (offered.has('email_code')) tabs.push('otp')
  const passkeyAsTab = passkey && tabs.length > 0
  if (passkeyAsTab) tabs.push('passkey')

  return {
    company: { name: options.company.name, logoUrl: options.company.logoUrl ?? null },
    hostKind: options.hostKind,
    tabs,
    passkeyAlone: !passkey || passkeyAsTab ? null : redirects.length > 0 ? 'link' : 'button',
    redirects,
    unavailable: options.unavailable
      .filter(isOrgMethod)
      .map((method) => ({ method, provider: ORG_METHOD_PROVIDER[method], label: labelOf(method) })),
    emergency: options.emergencyOffered === true,
  }
}

/** An answer only when it has the shape the page draws from; anything else is today's page. */
export function normalizeSignInOptions(raw: unknown): SignInOptions | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Partial<SignInOptions>
  if (r.scope !== 'platform' && r.scope !== 'company') return null
  if (!Array.isArray(r.offered)) return null
  const company =
    r.company && typeof r.company === 'object' && typeof r.company.name === 'string' && r.company.name.trim()
      ? { name: r.company.name.trim(), logoUrl: typeof r.company.logoUrl === 'string' && r.company.logoUrl ? r.company.logoUrl : null }
      : null
  return {
    audience: r.audience ?? 'admins',
    scope: r.scope === 'company' && company ? 'company' : 'platform',
    company,
    hostKind: r.hostKind === 'platform_subdomain' || r.hostKind === 'customer_cname' ? r.hostKind : 'platform',
    offered: r.offered.filter((k): k is SignInMethodKey => typeof k === 'string'),
    unavailable: Array.isArray(r.unavailable) ? r.unavailable.filter(isOrgMethod) : [],
    connections: Array.isArray(r.connections)
      ? r.connections.filter((c) => c && typeof c === 'object' && isOrgMethod(c.method) && typeof c.label === 'string')
      : [],
    emergencyOffered: r.emergencyOffered === true,
    platformUrl: bareHttpOrigin(r.platformUrl),
  }
}

/** `https://dash.humanfirewall.ai` from the answer's platformUrl, or null when it is not a bare http(s) origin. */
function bareHttpOrigin(value: unknown): string | null {
  if (typeof value !== 'string' || !value.trim()) return null
  try {
    const url = new URL(value.trim())
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.origin : null
  } catch {
    return null
  }
}

/** How long the page waits for the answer before it draws today's page instead. */
const OPTIONS_WAIT_MS = 4000

function withinWait<T>(promise: Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('sign-in options timed out')), OPTIONS_WAIT_MS)
    promise.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      (error: unknown) => {
        clearTimeout(timer)
        reject(error)
      },
    )
  })
}

/** The host this page is served on, as the API's lookup takes it (with the port, as the browser sends Origin). */
export function pageHost(): string {
  return typeof window === 'undefined' ? '' : window.location.host
}

/** "Which methods" for the address this page is served on. `checked` turns true once the answer, or no answer, is in. */
export function useHostSignInOptions(audience: SignInAudienceKey): { checked: boolean; options: SignInOptions | null } {
  const [state, setState] = useState<{ checked: boolean; options: SignInOptions | null }>({ checked: false, options: null })
  useEffect(() => {
    let live = true
    let request: Promise<unknown>
    try {
      request = Promise.resolve(fetchSignInOptions(audience, pageHost()))
    } catch (error) {
      request = Promise.reject(error)
    }
    withinWait(request)
      .then((raw) => live && setState({ checked: true, options: normalizeSignInOptions(raw) }))
      .catch(() => live && setState({ checked: true, options: null }))
    return () => {
      live = false
    }
  }, [audience])
  return state
}

/** "Which methods" for a typed work e-mail's domain (dash). Null for no answer. */
export async function signInOptionsForEmail(audience: SignInAudienceKey, email: string): Promise<SignInOptions | null> {
  try {
    return normalizeSignInOptions(await withinWait(Promise.resolve(fetchSignInOptionsByEmail(audience, pageHost(), email))))
  } catch {
    return null
  }
}

/** The domain of an e-mail address, lower-cased ('' when there is none). */
export function emailDomain(email: string): string {
  const at = email.lastIndexOf('@')
  return at < 0 ? '' : email.slice(at + 1).trim().toLowerCase()
}
